/**
 * Sync History Page
 *
 * Comprehensive view of historical sync operations with stats cards,
 * advanced filtering, pagination, and details modal.
 */

import React, { useState, useMemo, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button
} from '@mui/material'
import {
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  Cancel as CancelledIcon,
  Storage as RecordsIcon,
  Download as DownloadIcon,
  History as HistoryIcon
} from '@mui/icons-material'
import {
  SyncOperation,
  SyncHistoryFilters as ApiFilters,
  SyncHistoryResponse,
  SyncStats,
  syncApi
} from '../../../services/api'
import AdminPageHeader from '../../../components/admin/shared/AdminPageHeader'
import BaseDataTable from '../../../components/shared/BaseDataTable'
import ReportFilterCard from '../../../components/shared/filters/ReportFilterCard'
import { ReportFilters, FilterKey } from '../../../components/shared/filters/types'
import { useDataFetching } from '../../../hooks/useDataFetching'
import { poolwerxColors } from '../../../theme/colors'
import {
  SyncDetailsDialog,
  createSyncHistoryColumns
} from './components'

const ENABLED_FILTERS: FilterKey[] = ['search', 'system', 'initiatedBy', 'syncStatus', 'dateRange']

export const SyncHistory: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()

  // UI State
  const [selectedOperation, setSelectedOperation] = useState<SyncOperation | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(50)

  // Parse URL parameters for initial filters
  const getInitialFilters = useCallback((): ReportFilters => {
    const urlParams = new URLSearchParams(location.search)
    const fpCode = urlParams.get('fp_code')
    const system = urlParams.get('system')
    const status = urlParams.get('status')
    const startDate = urlParams.get('start_date')
    const endDate = urlParams.get('end_date')
    const initiatedBy = urlParams.get('initiated_by')

    return {
      ...(fpCode && { search: fpCode }),
      ...(system && { system }),
      ...(status && { syncStatus: status }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(initiatedBy && { initiatedBy })
    }
  }, [location.search])

  // Filters state - using ReportFilters type
  const [filters, setFilters] = useState<ReportFilters>(getInitialFilters())

  // Check if any filters are active
  const hasActiveFilters = Boolean(
    filters.search ||
    filters.system ||
    filters.syncStatus ||
    filters.startDate ||
    filters.endDate ||
    filters.initiatedBy
  )

  // Update URL when filters change
  const updateUrl = useCallback((newFilters: ReportFilters) => {
    const params = new URLSearchParams()
    if (newFilters.search) params.set('fp_code', newFilters.search)
    if (newFilters.system) params.set('system', newFilters.system)
    if (newFilters.syncStatus) params.set('status', newFilters.syncStatus)
    if (newFilters.startDate) params.set('start_date', newFilters.startDate)
    if (newFilters.endDate) params.set('end_date', newFilters.endDate)
    if (newFilters.initiatedBy) params.set('initiated_by', newFilters.initiatedBy)

    const queryString = params.toString()
    navigate(`${location.pathname}${queryString ? `?${queryString}` : ''}`, { replace: true })
  }, [navigate, location.pathname])

  // Fetch sync stats
  const {
    data: stats,
  } = useDataFetching<SyncStats>({
    fetchFn: async () => {
      const response = await syncApi.getStats()
      return response.data
    },
    dependencies: []
  })

  // Convert ReportFilters to API filters
  const apiFilters = useMemo((): ApiFilters => {
    const result: ApiFilters = {
      limit: perPage,
      offset: (currentPage - 1) * perPage
    }

    if (filters.search) result.fp_code = filters.search
    if (filters.system) result.system = filters.system
    if (filters.syncStatus) result.status = filters.syncStatus.split(',')
    if (filters.initiatedBy) result.initiated_by = filters.initiatedBy

    return result
  }, [filters, perPage, currentPage])

  // Fetch sync history
  const {
    data: historyData,
    loading: isLoading,
    error,
  } = useDataFetching<SyncHistoryResponse>({
    fetchFn: async () => {
      const apiResponse = await syncApi.getHistory(apiFilters)
      return apiResponse.data
    },
    dependencies: [apiFilters]
  })

  // Derived data
  const operations = historyData?.operations || []
  const totalCount = historyData?.total || 0

  // Event handlers
  const handleFilterChange = (key: keyof ReportFilters, value: string) => {
    const newFilters = { ...filters, [key]: value || undefined }
    setFilters(newFilters)
    setCurrentPage(1)
    updateUrl(newFilters)
  }

  const handleClearFilters = () => {
    const clearedFilters: ReportFilters = {}
    setFilters(clearedFilters)
    setCurrentPage(1)
    updateUrl(clearedFilters)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleViewDetails = useCallback((operation: SyncOperation) => {
    setSelectedOperation(operation)
    setDetailsOpen(true)
  }, [])

  // Create table columns
  const columns = useMemo(
    () => createSyncHistoryColumns(handleViewDetails),
    [handleViewDetails]
  )

  return (
    <>
      <AdminPageHeader
        pageKey="dataSync"
        titleOverride="Sync History"
        descriptionOverride="Comprehensive view of all sync operations"
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Data Sync', to: '/admin/data-sync/active' },
          { label: 'Sync History' },
        ]}
      />
      {/* Stats Cards - Single row with 4 key metrics */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                <CompletedIcon sx={{ fontSize: 32, color: 'success.main' }} />
                <Box>
                  <Typography color="textSecondary" variant="caption">
                    Completed Today
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    {stats.completed_today.toLocaleString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                <FailedIcon sx={{ fontSize: 32, color: 'error.main' }} />
                <Box>
                  <Typography color="textSecondary" variant="caption">
                    Failed Today
                  </Typography>
                  <Typography variant="h5" color="error.main">
                    {stats.failed_today.toLocaleString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                <CancelledIcon sx={{ fontSize: 32, color: 'warning.main' }} />
                <Box>
                  <Typography color="textSecondary" variant="caption">
                    Cancelled Today
                  </Typography>
                  <Typography variant="h5" color="warning.main">
                    {stats.cancelled_today.toLocaleString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                <RecordsIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
                <Box>
                  <Typography color="textSecondary" variant="caption">
                    Records Processed
                  </Typography>
                  <Typography variant="h5" color="secondary.main">
                    {stats.total_records_synced_today.toLocaleString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <ReportFilterCard
        filters={filters}
        enabledFilters={ENABLED_FILTERS}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
        loading={isLoading}
      />

      {/* Data Table */}
      <BaseDataTable
        variant="enhanced"
        title="Previous syncs"
        titleIcon={<HistoryIcon />}
        totalCount={totalCount}
        showCount
        countLabel="results"
        headerActions={
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{
              borderColor: '#E6E8EA',
              color: '#1F2A2E',
              px: 2,
              '&:hover': { borderColor: poolwerxColors.teal, backgroundColor: '#E6F4F5' }
            }}
          >
            Export
          </Button>
        }
        columns={columns}
        data={operations}
        loading={isLoading}
        error={error}
        emptyMessage={
          hasActiveFilters
            ? 'No sync operations match your current filters'
            : 'No sync history found'
        }
        pagination={{
          page: currentPage,
          perPage: perPage,
          total: totalCount,
          onPageChange: handlePageChange,
          showFirstLast: true,
          size: 'large'
        }}
        getRowKey={(row) => row.operation_id}
      />

      {/* Details Dialog */}
      <SyncDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        operation={selectedOperation}
      />
    </>
  )
}

export default SyncHistory
